import React, { createContext, useState, useContext, useEffect } from 'react';
import { auth, db } from '../config/firebase';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [guestCart, setGuestCart] = useState([]);
  const [isSyncing, setIsSyncing] = useState(false);

  // Track auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      
      if (currentUser) {
        // User logged in - load cart from Firestore
        await loadUserCart(currentUser.uid);
      } else {
        // Guest user - load from localStorage
        loadGuestCart();
      }
    });

    return () => unsubscribe();
  }, []);

  // Load guest cart from localStorage
  const loadGuestCart = () => {
    try {
      const savedCart = localStorage.getItem('guest_cart');
      if (savedCart) {
        const parsedCart = JSON.parse(savedCart);
        // Ensure each item has quantity property
        const cartWithQuantity = parsedCart.map(item => ({
          ...item,
          quantity: item.quantity || 1
        }));
        setGuestCart(cartWithQuantity);
        setCartItems(cartWithQuantity);
      } else {
        setGuestCart([]);
        setCartItems([]);
      }
    } catch (error) {
      console.error('Error loading guest cart:', error);
      setCartItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Save guest cart to localStorage
  const saveGuestCart = (items) => {
    localStorage.setItem('guest_cart', JSON.stringify(items));
    setGuestCart(items);
    setCartItems(items);
  };

  // Load user cart from Firestore
  const loadUserCart = async (userId) => {
    setLoading(true);
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const cart = userData.cart || [];
        // Ensure each item has quantity property
        const cartWithQuantity = cart.map(item => ({
          ...item,
          quantity: item.quantity || 1
        }));
        setCartItems(cartWithQuantity);
      } else {
        // Create user document if it doesn't exist
        await setDoc(userRef, {
          cart: [],
          wishlist: [],
          email: auth.currentUser?.email,
          createdAt: new Date().toISOString()
        });
        setCartItems([]);
      }
    } catch (error) {
      console.error('Error loading user cart:', error);
      setCartItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Merge guest cart with user cart after login
  const mergeGuestCartWithUser = async () => {
    if (!user || guestCart.length === 0) return;
    
    setIsSyncing(true);
    try {
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      const existingCart = userDoc.exists() ? (userDoc.data().cart || []) : [];
      
      // Merge carts - combine items based on productId with quantity
      const mergedCart = [...existingCart];
      
      guestCart.forEach(guestItem => {
        const existingIndex = mergedCart.findIndex(item => item.productId === guestItem.productId);
        
        if (existingIndex !== -1) {
          // Item exists, merge quantities (add together, max 10)
          const existingItem = mergedCart[existingIndex];
          const mergedQuantity = Math.min((existingItem.quantity || 1) + (guestItem.quantity || 1), 10);
          mergedCart[existingIndex] = {
            ...existingItem,
            quantity: mergedQuantity,
            addedAt: Math.max(new Date(existingItem.addedAt).getTime(), new Date(guestItem.addedAt).getTime()),
            updatedAt: new Date().toISOString()
          };
        } else {
          // New item, add to cart
          mergedCart.push({
            ...guestItem,
            quantity: guestItem.quantity || 1
          });
        }
      });
      
      // Update Firestore with merged cart
      await updateDoc(userRef, {
        cart: mergedCart,
        updatedAt: new Date().toISOString()
      });
      
      // Clear guest cart
      localStorage.removeItem('guest_cart');
      setGuestCart([]);
      setCartItems(mergedCart);
      
    } catch (error) {
      console.error('Error merging carts:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  // Sync when user logs in
  useEffect(() => {
    if (user) {
      mergeGuestCartWithUser();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Add to cart with quantity support
  const addToCart = async (product, quantity = 1) => {
    const validQuantity = Math.max(1, Math.min(quantity, 10));
    
    const newItem = {
      productId: product.productId || product.id,
      addedAt: new Date().toISOString(),
      quantity: validQuantity
    };

    if (!user) {
      // Guest user - update localStorage
      const existingItemIndex = guestCart.findIndex(item => item.productId === newItem.productId);
      let updatedCart;
      
      if (existingItemIndex !== -1) {
        // Item already exists, add to quantity (max 10)
        const existingItem = guestCart[existingItemIndex];
        const newQuantity = Math.min((existingItem.quantity || 1) + validQuantity, 10);
        updatedCart = [...guestCart];
        updatedCart[existingItemIndex] = {
          ...existingItem,
          quantity: newQuantity,
          addedAt: newItem.addedAt
        };
      } else {
        updatedCart = [...guestCart, newItem];
      }
      
      saveGuestCart(updatedCart);
      return true;
    }

    // Authenticated user - update Firestore
    try {
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      const currentCart = userDoc.exists() ? (userDoc.data().cart || []) : [];
      
      const existingItemIndex = currentCart.findIndex(item => item.productId === newItem.productId);
      let updatedCart;
      
      if (existingItemIndex !== -1) {
        // Item already exists, add to quantity (max 10)
        const existingItem = currentCart[existingItemIndex];
        const newQuantity = Math.min((existingItem.quantity || 1) + validQuantity, 10);
        updatedCart = [...currentCart];
        updatedCart[existingItemIndex] = {
          ...existingItem,
          quantity: newQuantity,
          addedAt: newItem.addedAt,
          updatedAt: new Date().toISOString()
        };
      } else {
        updatedCart = [...currentCart, newItem];
      }
      
      await updateDoc(userRef, {
        cart: updatedCart,
        updatedAt: new Date().toISOString()
      });
      
      setCartItems(updatedCart);
      return true;
      
    } catch (error) {
      console.error('Error adding to cart:', error);
      
      // If user document doesn't exist, create it
      if (error.code === 'not-found') {
        try {
          const userRef = doc(db, 'users', user.uid);
          await setDoc(userRef, {
            cart: [newItem],
            wishlist: [],
            email: user.email,
            createdAt: new Date().toISOString()
          });
          
          setCartItems([newItem]);
          return true;
        } catch (createError) {
          console.error('Error creating user document:', createError);
          return false;
        }
      }
      
      return false;
    }
  };

  // Update quantity of a cart item
  const updateQuantity = async (productId, newQuantity) => {
    const validQuantity = Math.max(1, Math.min(newQuantity, 10));
    
    if (!user) {
      // Guest user
      const updatedCart = guestCart.map(item =>
        item.productId === productId 
          ? { ...item, quantity: validQuantity, updatedAt: new Date().toISOString() } 
          : item
      );
      saveGuestCart(updatedCart);
      return true;
    }
    
    // Authenticated user
    try {
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      const currentCart = userDoc.exists() ? (userDoc.data().cart || []) : [];
      
      const updatedCart = currentCart.map(item =>
        item.productId === productId 
          ? { ...item, quantity: validQuantity, updatedAt: new Date().toISOString() } 
          : item
      );
      
      await updateDoc(userRef, {
        cart: updatedCart,
        updatedAt: new Date().toISOString()
      });
      
      setCartItems(updatedCart);
      return true;
      
    } catch (error) {
      console.error('Error updating quantity:', error);
      return false;
    }
  };

  // Update cart item (update timestamp)
  const updateCartItem = async (productId) => {
    if (!user) {
      // Guest user
      const updatedCart = guestCart.map(item =>
        item.productId === productId 
          ? { ...item, addedAt: new Date().toISOString(), updatedAt: new Date().toISOString() } 
          : item
      );
      saveGuestCart(updatedCart);
      return;
    }
    
    // Authenticated user
    try {
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      const currentCart = userDoc.exists() ? (userDoc.data().cart || []) : [];
      
      const updatedCart = currentCart.map(item =>
        item.productId === productId 
          ? { ...item, addedAt: new Date().toISOString(), updatedAt: new Date().toISOString() } 
          : item
      );
      
      await updateDoc(userRef, {
        cart: updatedCart,
        updatedAt: new Date().toISOString()
      });
      
      setCartItems(updatedCart);
      
    } catch (error) {
      console.error('Error updating cart item:', error);
    }
  };

  // Remove from cart
  const removeFromCart = async (productId) => {
    if (!user) {
      // Guest user
      const updatedCart = guestCart.filter(item => item.productId !== productId);
      saveGuestCart(updatedCart);
      return true;
    }
    
    // Authenticated user
    try {
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      const currentCart = userDoc.exists() ? (userDoc.data().cart || []) : [];
      
      const updatedCart = currentCart.filter(item => item.productId !== productId);
      
      await updateDoc(userRef, {
        cart: updatedCart,
        updatedAt: new Date().toISOString()
      });
      
      setCartItems(updatedCart);
      return true;
      
    } catch (error) {
      console.error('Error removing from cart:', error);
      return false;
    }
  };

  // Clear entire cart
  const clearCart = async () => {
    if (!user) {
      // Guest user
      saveGuestCart([]);
      return;
    }
    
    // Authenticated user
    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        cart: [],
        updatedAt: new Date().toISOString()
      });
      
      setCartItems([]);
      
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
  };

  // Get cart total quantity count (sum of all quantities)
  const getCartTotalQuantity = () => {
    return cartItems.reduce((total, item) => total + (item.quantity || 1), 0);
  };

  // Get cart item count (number of unique products)
  const getCartCount = () => {
    return cartItems.length;
  };

  // Get quantity for a specific product
  const getProductQuantity = (productId) => {
    const item = cartItems.find(item => item.productId === productId);
    return item ? (item.quantity || 1) : 0;
  };

  // Check if product is in cart
  const isInCart = (productId) => {
    return cartItems.some(item => item.productId === productId);
  };

  // Get cart total (requires fetching product details)
  // This function will need to fetch product details from Firestore
  const getCartTotal = async () => {
    // Since we only store productId, we need to fetch product details
    // This would require an additional API call to get product prices
    // You might want to cache product details or fetch on demand
    return 0; // Placeholder
  };

  // Get selected items total (requires fetching product details)
  const getSelectedItemsTotal = async () => {
    // Similar to getCartTotal, needs product details
    return 0; // Placeholder
  };

  // Sync cart manually (useful after login)
  const syncCart = async () => {
    if (user) {
      await mergeGuestCartWithUser();
    }
  };

  const value = {
    cartItems,
    loading,
    isSyncing,
    addToCart,
    updateQuantity,      // New: Update quantity of a cart item
    updateCartItem,
    removeFromCart,
    clearCart,
    getCartTotal,
    getCartCount,
    getCartTotalQuantity, // New: Get total quantity of all items
    getProductQuantity,   // New: Get quantity of specific product
    isInCart,            // New: Check if product is in cart
    getSelectedItemsTotal,
    syncCart,
    user,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};